import axios from 'axios';

// Создаем инстанс
export const apiClient = axios.create({
  baseURL: '/api',
});

// Добавляем Access Token в каждый запрос
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Логика обновления токена
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      
      if (originalRequest.url.includes('/auth/refresh')) {
        auth.logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
        .then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        // Обращаемся по правильному адресу: /auth/refresh (так как baseURL=/api)
        const { data } = await axios.post('/api/auth/refresh', { refreshToken });
        
        const newToken = data.token;
        localStorage.setItem('token', newToken);

        // Обновляем кеш user из нового токена
        try {
          const payload = JSON.parse(atob(newToken.split('.')[1]));
          localStorage.setItem('cachedUser', JSON.stringify(payload));
        } catch (e) { /* ignore */ }
        
        apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        processQueue(null, newToken);
        
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        return apiClient(originalRequest);
        
      } catch (err) {
        processQueue(err, null);
        auth.logout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const auth = {
  // Важно: здесь путь /auth/login
  login: async (email, password, rememberMe) => {
    const response = await apiClient.post('/auth/login', { email, password, rememberMe });
    const { token, refreshToken, expiresIn } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('rememberMe', rememberMe.toString()); 
    localStorage.setItem('lastActivity', Date.now().toString());

    // Декодируем JWT payload и кешируем user для мгновенного рендера меню
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      localStorage.setItem('cachedUser', JSON.stringify(payload));
    } catch (e) { /* ignore */ }
    
    return response;
  },
  
  register: (userData) => apiClient.post('/register', userData),
  me: () => apiClient.get('/auth/me'),
  
  logout: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await axios.post('/api/auth/logout', { refreshToken });
      }
    } finally {
      localStorage.clear();
      window.location.href = '/login';
    }
  },
};

// ... entities и integrations остаются без изменений ...
// Просто убедитесь, что вы не удалили export const entities = { ... }
export const entities = {
  Project: {
    list: (sort = '-created_date') => apiClient.get('/projects', { params: { sort } }).then(res => res.data),
    create: (data) => apiClient.post('/projects', data).then(res => res.data),
    update: (id, data) => apiClient.put(`/projects/${id}`, data).then(res => res.data),
    delete: (id) => apiClient.delete(`/projects/${id}`).then(res => res.data),
  },
  Inspection: {
    list: (sort = '-inspection_date') => apiClient.get('/inspections', { params: { sort } }).then(res => res.data),
    create: (data) => apiClient.post('/inspections', data).then(res => res.data),
    update: (id, data) => apiClient.put(`/inspections/${id}`, data).then(res => res.data),
    delete: (id) => apiClient.delete(`/inspections/${id}`).then(res => res.data),
    downloadDocument: (id) => apiClient.get(`/inspections/${id}/document`, { responseType: 'blob' }),
  },
  Defect: {
    list: (sort = '-created_date') => apiClient.get('/defects', { params: { sort } }).then(res => res.data),
    filter: (filters) => apiClient.get('/defects', { params: filters }).then(res => res.data),
    create: (data) => apiClient.post('/defects', data).then(res => res.data),
    update: (id, data) => apiClient.put(`/defects/${id}`, data).then(res => res.data),
    delete: (id) => apiClient.delete(`/defects/${id}`).then(res => res.data),
  },
  User: {
    list: (sort = '-created_date') => apiClient.get('/users', { params: { sort } }).then(res => res.data),
    update: (id, data) => apiClient.put(`/users/${id}`, data).then(res => res.data),
    updateCredentials: (id, data) => apiClient.put(`/users/${id}/credentials`, data).then(res => res.data),
    delete: (id) => apiClient.delete(`/users/${id}`).then(res => res.data),
  },
  Notification: {
    list: (params = {}) => apiClient.get('/notifications', { params }).then(res => res.data),
    markAllRead: () => apiClient.put('/notifications/read-all'),
    markOneRead: (id) => apiClient.put(`/notifications/${id}/read`),
  },
  InspectionRequest: {
      list: () => apiClient.get('/requests').then(res => res.data),
      create: (data) => apiClient.post('/requests', data).then(res => res.data),
      accept: (id) => apiClient.put(`/requests/${id}/accept`).then(res => res.data),
      convertToInspection: (id) => apiClient.post(`/requests/${id}/convert`).then(res => res.data),
      delete: (id) => apiClient.delete(`/requests/${id}`).then(res => res.data),
  },
  Gpr: {
    list: (projectId, opts = {}) => {
      const params = { project_id: projectId };
      if (opts.withInspectionCounts) params.with_inspection_counts = 'true';
      return apiClient.get('/gpr', { params }).then(res => res.data);
    },
    get: (id) => apiClient.get(`/gpr/${id}`).then(res => res.data),
    dashboard: () => apiClient.get('/gpr/dashboard').then(res => res.data),
    create: (data) => apiClient.post('/gpr', data).then(res => res.data),
    update: (id, data) => apiClient.put(`/gpr/${id}`, data).then(res => res.data),
    delete: (id) => apiClient.delete(`/gpr/${id}`).then(res => res.data),
  },
  Organization: {
    list: () => apiClient.get('/users/organizations').then(res => res.data),
  },
};

export const integrations = {
  Core: {
    UploadFile: async ({ file }) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  },
};